// src/components/PendenzResponsibleCreateModal.jsx
// Legt aus der Verantwortlichen-Auswahl (Pendenzen) eine neue Organisation oder
// Person im CRM an und ordnet sie dem aktuellen Projekt zu – konsistent zum
// Netzwerk-Tab (Relation über project.code) und zur CRM-Erfassung (gleiche Services).
// Es wird gefragt, ob die Projekt-Verbindung öffentlich sichtbar ist.
import React, { useState } from "react";
import { createPartner } from "../services/azurePartnersService";
import { createPerson } from "../services/azurePersonsService";
import {
  createRelation,
  RELATION_TYPES,
} from "../services/azureRelationsService";

const splitName = (full) => {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

const PendenzResponsibleCreateModal = ({
  type, // "organization" | "person"
  initialName,
  partners = [],
  projectCode,
  // Optional: konkrete Rolle (z.B. RELATION_TYPES.AUFTRAGGEBERIN) statt der
  // generischen PROJECT_PARTNER/PROJECT_PERSON-Relation aus der Pendenzen-Nutzung.
  relationType,
  onCreated,
  onClose,
}) => {
  const isPerson = type === "person";
  const seed = isPerson ? splitName(initialName) : { firstName: "", lastName: "" };

  const [form, setForm] = useState({
    // Organisation
    name: isPerson ? "" : initialName || "",
    website: "",
    // Person
    firstName: seed.firstName,
    lastName: seed.lastName,
    organization: "",
    position: "",
    mobile: "",
    anrede: "du",
    // gemeinsam
    email: "",
    phone: "",
    addressStrasse: "",
    addressPlz: "",
    addressOrt: "",
    addressLand: "Schweiz",
    tags: "",
    notes: "",
    // Projekt-Verbindung
    funktion: "",
  });
  // Organisationen sind standardmässig öffentlich, Personen privat (CRM-Konvention)
  const [oeffentlich, setOeffentlich] = useState(!isPerson);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    // Siehe PendenzResponsibleLinkModal: Submit darf nicht zum umgebenden
    // Formular (z.B. ProjectForm) aufsteigen.
    e.stopPropagation();
    if (isPerson) {
      if (!form.firstName.trim() || !form.lastName.trim()) {
        setError("Vor- und Nachname sind erforderlich.");
        return;
      }
    } else if (!form.name.trim()) {
      setError("Name der Organisation ist erforderlich.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const common = {
        email: form.email.trim(),
        phone: form.phone.trim(),
        addressStrasse: form.addressStrasse.trim(),
        addressPlz: form.addressPlz.trim(),
        addressOrt: form.addressOrt.trim(),
        addressLand: form.addressLand.trim() || "Schweiz",
        tags: form.tags.trim(),
        notes: form.notes.trim(),
      };

      let entity;
      let option;
      let extraRelation = null;

      if (isPerson) {
        entity = await createPerson({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          mobile: form.mobile.trim(),
          position: form.position.trim(),
          organization: form.organization || "",
          anrede: form.anrede || "du",
          ...common,
        });
        const label = `${entity.firstName} ${entity.lastName}`.trim();
        option = { id: `person:${entity.id}`, label, group: "Person" };
      } else {
        entity = await createPartner({
          name: form.name.trim(),
          type: "Organisation",
          website: form.website.trim(),
          ...common,
        });
        option = { id: `org:${entity.id}`, label: entity.name, group: "Organisation" };
      }

      // Relation Projekt ↔ Entität – project.code als sourceId (wie Netzwerk-Tab),
      // mit gewählter Sichtbarkeit.
      const relation = await createRelation({
        sourceType: "project",
        sourceId: projectCode,
        targetType: isPerson ? "person" : "partner",
        targetId: entity.id,
        relationType:
          relationType ||
          (isPerson ? RELATION_TYPES.PROJECT_PERSON : RELATION_TYPES.PROJECT_PARTNER),
        function: form.funktion.trim(),
        description: `${isPerson ? "Person" : "Organisation"} in Projekt (über Pendenzen)`,
        visibility: oeffentlich,
      });

      // Person ↔ Organisation verknüpfen (falls eine bestehende Organisation gewählt),
      // analog ProjectNetworkManager.
      if (isPerson && form.organization) {
        const org = partners.find(
          (p) => p.name.toLowerCase() === form.organization.toLowerCase()
        );
        if (org) {
          extraRelation = await createRelation({
            sourceType: "partner",
            sourceId: org.id,
            targetType: "person",
            targetId: entity.id,
            relationType: RELATION_TYPES.PARTNER_PERSON,
            function: form.position.trim(),
            description: "Zuordnung über Pendenzen",
          });
        }
      }

      onCreated({ type: isPerson ? "person" : "organization", entity, relation, extraRelation, option });
    } catch (err) {
      console.error("Verantwortliche anlegen fehlgeschlagen:", err);
      setError(
        err.message ||
          "Die/der Verantwortliche konnte nicht angelegt werden."
      );
      setSaving(false);
    }
  };

  // Weder Klick auf den Hintergrund noch Escape schliessen (Nutzerwunsch, ADR
  // 0060): kein versehentliches Verwerfen ungespeicherter Eingaben.
  return (
    <div
      className="pendenzen__modal-overlay"
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
    >
      <div
        className="pendenzen__modal pendenzen__modal--share"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSave}>
          <div className="pendenzen__modal-header">
            <h3>
              {isPerson ? "Neue Person anlegen" : "Neue Organisation anlegen"}
            </h3>
            <button
              type="button"
              className="pendenzen__modal-close"
              onClick={onClose}
              aria-label="Schliessen"
            >
              ×
            </button>
          </div>

          <div className="pendenzen__modal-body">
            {error && (
              <div className="pendenzen__error" role="alert" onClick={() => setError(null)}>
                {error}
              </div>
            )}

            <div className="pendenzen__form-grid">
              {isPerson ? (
                <>
                  <label className="pendenzen__field">
                    <span>Vorname *</span>
                    <input
                      type="text"
                      value={form.firstName}
                      onChange={(e) => set("firstName", e.target.value)}
                      autoFocus
                    />
                  </label>
                  <label className="pendenzen__field">
                    <span>Nachname *</span>
                    <input
                      type="text"
                      value={form.lastName}
                      onChange={(e) => set("lastName", e.target.value)}
                    />
                  </label>
                  <label className="pendenzen__field">
                    <span>Organisation</span>
                    <select
                      value={form.organization}
                      onChange={(e) => set("organization", e.target.value)}
                    >
                      <option value="">Keine Organisation</option>
                      {partners
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                        .map((p) => (
                          <option key={p.id} value={p.name}>
                            {p.name}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="pendenzen__field">
                    <span>Position</span>
                    <input
                      type="text"
                      value={form.position}
                      onChange={(e) => set("position", e.target.value)}
                      placeholder="z.B. PL, Sachbearbeitung"
                    />
                  </label>
                  <label className="pendenzen__field">
                    <span>Mobile</span>
                    <input
                      type="tel"
                      value={form.mobile}
                      onChange={(e) => set("mobile", e.target.value)}
                    />
                  </label>
                  <label className="pendenzen__field">
                    <span>Anrede</span>
                    <select
                      value={form.anrede}
                      onChange={(e) => set("anrede", e.target.value)}
                    >
                      <option value="du">Du</option>
                      <option value="sie">Sie</option>
                    </select>
                  </label>
                </>
              ) : (
                <>
                  <label className="pendenzen__field pendenzen__field--full">
                    <span>Name *</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      autoFocus
                    />
                  </label>
                  <label className="pendenzen__field">
                    <span>Website</span>
                    <input
                      type="url"
                      value={form.website}
                      onChange={(e) => set("website", e.target.value)}
                      placeholder="https://..."
                    />
                  </label>
                </>
              )}

              <label className="pendenzen__field">
                <span>E-Mail</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </label>
              <label className="pendenzen__field">
                <span>Telefon</span>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                />
              </label>

              <label className="pendenzen__field pendenzen__field--full">
                <span>Strasse</span>
                <input
                  type="text"
                  value={form.addressStrasse}
                  onChange={(e) => set("addressStrasse", e.target.value)}
                />
              </label>
              <label className="pendenzen__field">
                <span>PLZ</span>
                <input
                  type="text"
                  value={form.addressPlz}
                  onChange={(e) => set("addressPlz", e.target.value)}
                />
              </label>
              <label className="pendenzen__field">
                <span>Ort</span>
                <input
                  type="text"
                  value={form.addressOrt}
                  onChange={(e) => set("addressOrt", e.target.value)}
                />
              </label>
              <label className="pendenzen__field">
                <span>Land</span>
                <input
                  type="text"
                  value={form.addressLand}
                  onChange={(e) => set("addressLand", e.target.value)}
                />
              </label>

              <label className="pendenzen__field">
                <span>Funktion im Projekt</span>
                <input
                  type="text"
                  value={form.funktion}
                  onChange={(e) => set("funktion", e.target.value)}
                  placeholder="z.B. Bauleitung"
                />
              </label>
              <label className="pendenzen__field">
                <span>Tags</span>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => set("tags", e.target.value)}
                  placeholder="kommagetrennt"
                />
              </label>

              <label className="pendenzen__field pendenzen__field--full">
                <span>Notizen</span>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                />
              </label>

              <label className="pendenzen__field pendenzen__field--checkbox pendenzen__field--full">
                <input
                  type="checkbox"
                  checked={oeffentlich}
                  onChange={(e) => setOeffentlich(e.target.checked)}
                />
                <span>Verbindung mit dem Projekt öffentlich sichtbar</span>
              </label>
            </div>
          </div>

          <div className="pendenzen__modal-footer">
            <div className="pendenzen__modal-footer-right">
              <button
                type="button"
                className="pendenzen__btn"
                onClick={onClose}
                disabled={saving}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="pendenzen__btn pendenzen__btn--primary"
                disabled={saving}
              >
                {saving ? "Speichern..." : "Anlegen & zuordnen"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PendenzResponsibleCreateModal;
